import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import {
  LayoutDashboard,
  CalendarDays,
  Star,
  User,
  Wallet,
  Settings,
  LogOut,
  Bell,
  X,
  HelpCircle,
} from "lucide-react";

export default function Sidebar({ open, onClose }) {
  const { logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState(null);
  const objectUrlRef = useRef(null);

  const navItems = [
    { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { path: "/bookings", labelKey: "nav.bookings", icon: CalendarDays },
    { path: "/reviews", labelKey: "nav.reviews", icon: Star },
    { path: "/notifications", labelKey: "nav.notifications", icon: Bell, badge: unreadCount },
    { path: "/profile", labelKey: "nav.profile", icon: User },
    { path: "/finance", labelKey: "nav.payments", icon: Wallet },
    { path: "/settings", labelKey: "nav.settings", icon: Settings },
    { path: "/help", labelKey: "nav.help", icon: HelpCircle },
  ];

  const initials = user?.firstName
    ? user.firstName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "HG";

  // Fetch profile picture as a blob from the authenticated stream endpoint
  useEffect(() => {
    const fetchPicture = async () => {
      try {
        const { data: profile } = await nestApi.get("/rahe-portal/profile");
        if (!profile.has_picture) return;
        const res = await nestApi.get("/rahe-portal/profile/picture", {
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setProfilePicture(url);
      } catch {
        // Silently fail
      }
    };
    fetchPicture();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [location.pathname]);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-full w-64 bg-[#1A1B2E] text-white flex flex-col z-50 shadow-xl
          transition-transform duration-300 ease-in-out
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        `}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/logo.svg" alt="Rahe Portal" className="h-7" />
          </button>
          <button
            className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`sidebar-${item.path.replace("/", "")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{t(item.labelKey)}</span>
                {item.badge > 0 && (
                  <span className="min-w-[20px] h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1.5">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 pb-2">
          <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-white/5 mb-2">
            {profilePicture ? (
              <img 
                src={profilePicture} 
                alt="Profile" 
                className="w-8 h-8 rounded-full object-cover shrink-0"
                data-testid="sidebar-profile-pic"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold shrink-0">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.firstName || "Guide"}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email || ""}</p>
            </div>
          </div>
        </div>

        <div className="px-3 pb-3 border-t border-white/10 pt-2">
          <button
            onClick={logout}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5" />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
